import type { CogniteFunctionDefinition } from '#core/hooks/fusion/useCogniteFunction';
import { z } from 'zod';

export const FastSiRequest = z.object({
  model_key: z.enum(['si']).default('si').describe('The key of the model to be used for prediction'),
  ml_inputs: z.object({
    pressure: z
      .number()
      .min(0, { error: 'Pressure must be in range 0~4000 psi' })
      .max(4000, { error: 'Pressure must be in range 0~4000 psi' })
      .describe('Wellbore pressure in psi'),
    reservoir: z.string({ error: 'Reservoir must be specified' }).describe('Reservoir identifier'),
    temperature: z
      .number()
      .min(0, { error: 'Temperature must be in range 0~350 °F' })
      .max(350, { error: 'Temperature must be in range 0~350 °F' })
      .describe('Wellbore temperature in degrees Fahrenheit'),
    specific_gravity: z
      .number()
      .min(1, { error: 'Specific Gravity must be in range 1.0~2.0' })
      .max(2, { error: 'Specific Gravity must be in range 1.0~2.0' })
      .describe('Specific gravity of the fluid sample'),
  }),
});

export type FastSiRequest = z.input<typeof FastSiRequest>;

export const FastSiResponse = z.object({
  model: z.enum(['reservoir_chemistry']).describe('The key of the model used for prediction'),
  predictions: z.object({
    chemistry_imputation: z.object({
      so4: z.object({
        uncertainty: z.number().describe('Uncertainty of predicted concentration of Sulfate (SO4)'),
        value: z.number().describe('Predicted concentration of Sulfate (SO4)'),
      }),
      sr: z.object({
        uncertainty: z.number().describe('Uncertainty of predicted concentration of Strontium (Sr)'),
        value: z.number().describe('Predicted concentration of Strontium (Sr)'),
      }),
    }),
    classifier_explanations: z
      .array(
        z.object({
          feature: z.string().describe('Feature name used in the classification model'),
          shap_value: z.number().describe('SHAP value indicating feature impact on classification'),
        }),
      )
      .describe('Explanations for the classification model using SHAP values'),
    regressor_explanations: z
      .array(
        z.object({
          feature: z.string().describe('Feature name used in the regression model'),
          shap_value: z.number().describe('SHAP value indicating feature impact on regression'),
        }),
      )
      .describe('Explanations for the regression model using SHAP values'),
    si_max: z.number().describe('Maximum predicted Scale Index (SI) value'),
    si_min: z.number().describe('Minimum predicted Scale Index (SI) value'),
    si_pred: z.number().describe('Predicted Scale Index (SI) value'),
    zone_label: z.string().describe('Label of the predicted scaling risk zone'),
    zone_pred: z.number().describe('Predicted scaling risk zone as an integer'),
    zone_proba: z.array(z.number()).describe('Probabilities for each scaling risk zone'),
  }),
});

export type FastSiResponse = z.input<typeof FastSiResponse>;

export const FastSiFunction = {
  externalId: 'fn_scale_poc_ml_predict_uc2',
  request: FastSiRequest,
  response: FastSiResponse,
} as const satisfies CogniteFunctionDefinition;

export type FastSiFunction = typeof FastSiFunction;
